/**
 * TwinScoreHero — iter614
 * Hero version of Twin Score (composite 0-100 from 7 close twins) for the
 * dashboard top. Sister component to HiScoreHero — same dimensions and feel.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, Loader2 } from "lucide-react";
import { useHealthEvent } from "@/utils/healthEvents";

export default function TwinScoreHero({ API, isDark, bg = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchScore = useCallback((force = false) => {
    const url = `${API}/health-twin/twin-score?days=30${force ? "&force_refresh=true" : ""}`;
    return axios.get(url, { timeout: 15000 })
      .then(({ data: d }) => { setData(d); })
      .catch(() => {})
      .finally(() => { setLoading(false); });
  }, [API]);

  useEffect(() => {
    fetchScore(false);
  }, [fetchScore]);

  // iter760 — Live re-compute on structured condition change (force refresh
  // to bypass the 5-min server cache — cache is already invalidated on
  // POST/DELETE but we double-guard here).
  useHealthEvent("conditions-changed", () => { fetchScore(true); });

  if (loading && !data) {
    return (
      <div className={`p-5 rounded-2xl border ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"} flex items-center justify-center min-h-[120px]`}
           data-testid="twin-score-hero">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
      </div>
    );
  }
  if (!data) return null;

  const score = data.composite ?? 0;
  const color = score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  const subKeys = [
    { id: "nutritional", label: bg ? "Хран." : "Nutri", color: "#10b981" },
    { id: "cognitive", label: bg ? "Когн." : "Cog", color: "#6366f1" },
    { id: "lab", label: bg ? "Лаб." : "Lab", color: "#0ea5e9" },
    { id: "phenotype", label: bg ? "Фенотип" : "Pheno", color: "#a855f7" },
    { id: "microbiome", label: bg ? "Микро" : "Micro", color: "#22c55e" },
    { id: "body_comp", label: bg ? "Тяло" : "Body", color: "#f59e0b" },
    { id: "sleep", label: bg ? "Сън" : "Sleep", color: "#818cf8" },
    { id: "stress", label: bg ? "Стрес" : "Stress", color: "#f97316" },
  ];

  return (
    <button
      onClick={() => navigate("/dashboard")}
      data-testid="twin-score-hero"
      className={`w-full text-left p-5 rounded-2xl border transition-all hover:shadow-lg ${
        isDark ? "bg-gradient-to-br from-indigo-900/40 via-purple-900/30 to-fuchsia-900/30 border-indigo-500/30"
               : "bg-gradient-to-br from-indigo-50 via-purple-50 to-fuchsia-50 border-indigo-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            <div className={`text-[11px] uppercase font-bold ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
              {bg ? "Twin Score · изход на здравето" : "Twin Score · health outcome"}
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-5xl font-bold" style={{ color }} data-testid="twin-score-hero-value">
              {score}
            </div>
            <div className="text-sm opacity-50">/100</div>
            <div className="text-[10px] opacity-60 ml-2">{data.coverage_pct}% покритие</div>
          </div>
          <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {data.interpretation_bg}
          </p>
        </div>
        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
      </div>

      <div className="grid grid-cols-8 gap-1 mt-3">
        {subKeys.map((t) => {
          const v = data.sub_scores?.[t.id];
          return (
            <div key={t.id} className="text-center" data-testid={`twin-score-hero-sub-${t.id}`}>
              <div className={`h-1 rounded-full ${isDark ? "bg-slate-800" : "bg-slate-200"} overflow-hidden`}>
                {v !== null && (
                  <div className="h-full transition-all" style={{ width: `${v}%`, background: t.color }} />
                )}
              </div>
              <div className="text-[8px] mt-0.5 opacity-60 truncate" title={t.label}>
                {t.label} {v !== null && v !== undefined ? v : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </button>
  );
}
